/**
 * ******************************************************
 * Copyright (C) 2015-2021 VMware, Inc. All rights reserved.
 * *******************************************************
 *
 * @format
 */

/**
 * @fileoverview rtav-service.js -- RTAVService
 * Service implement RTAV feature for WebClient
 *
 * It was using RTAV namespace.
 * Now the structure looks weird after refactory...
 */
import { Injectable } from "@angular/core";
import RTAV from "./logservice";
import "./rtavconstants";
import "./rtavsettings";
import DeviceMessageManager from "./devicemessagemanager";
import dialogService from "./dialogservice";
import SyncTimer from "./synctimer";
import FrameRateController from "./frameratecontroller";
import MessageWaitManager from "./messagewaitmanager";
import AudioCapture from "./audiocapture";
import VideoCapture from "./videocapture";
import MediaCapture from "./mediacapture";
import "./typedarraysupport";
import MediaEncoder from "./mediaencoder";
import resourceManager from "./resourcemanager";
import rtavProtocolService from "./rtavprotocolservice";
import RtavSessionController from "./rtavsessioncontroller";
import RtavSession from "./rtavsession";
import wmksAdapter from "./wmksadapter";
import { PreferredRTAVDeviceService } from "../../common/service/preferred-RTAV-device.service";
import {
   SessionMsg,
   RemoteSessionEventService
} from "../../common/remote-session/remote-session-event.service";
import { BlastWmks } from '../common/blast-wmks.service';
import { ModalDialogService } from "../../common/commondialog/dialog.service";
import { clientUtil } from "../../../core/libs";

RTAV.dialogService = dialogService;
RTAV.SyncTimer = SyncTimer;
RTAV.FrameRateController = FrameRateController;
RTAV.MessageWaitManager = MessageWaitManager;
RTAV.DeviceMessageManager = DeviceMessageManager;
RTAV.AudioCapture = AudioCapture;
RTAV.VideoCapture = VideoCapture;
RTAV.MediaCapture = MediaCapture;
RTAV.MediaEncoder = MediaEncoder;
RTAV.resourceManager = resourceManager;
RTAV.rtavProtocolService = rtavProtocolService;
RTAV.RtavSessionController = RtavSessionController;
RTAV.RtavSession = RtavSession;
RTAV.wmksAdapter = wmksAdapter;
// bypass angular
RTAV.preferredDeviceService = new PreferredRTAVDeviceService();

export type DeviceStatus = {
   activeSessionId: string;
   deviceInUsed: {
      audio: boolean;
      video: boolean;
   }
};


@Injectable()
export class RTAVService {
   constructor(
      private remoteSessionEventService: RemoteSessionEventService,
      private modalDialogService: ModalDialogService
   ) {
      if (!clientUtil.isChromeClient()) {
         this.setDialogHandler(this._confirmRTAVSessionSwitch);
      }
   }

   public init(rtavInitArgs?) {
      RTAV.logService.log("constructing RTAV service");
      if (!!rtavInitArgs) {
         RTAV.logService.debug("video codec enable H264 is " + rtavInitArgs.enableRTAVH264Codec);
         RTAV.logService.debug("audio codec enable Opus is " + rtavInitArgs.enableRTAVOpusCodec);
         RTAV.logService.debug("RTAV DTX mode is: " + rtavInitArgs.enableRTAVDTX);
         RTAV.logService.debug("video hardware acceleration option value get from admin is " + rtavInitArgs.hardwareAccelerationOption);
      }
      if (this.remoteSessionEventService) {
         this.remoteSessionEventService.addEventListener(
            SessionMsg.SESSION_CONNECTING_MSG,
            (session: BlastWmks)=> {
               if (!!rtavInitArgs) {
                  this.onConnecting(session.key, session.vdpService.vvcSession, rtavInitArgs);
               } else {
                  this.onConnecting(session.key, session.vdpService.vvcSession);
               }
            }
         );
         this.remoteSessionEventService.addEventListener(
            SessionMsg.SESSION_CONNECTED_MSG,
            (session: BlastWmks)=> {
               this.onConnected(session.key);
            }
         );
         this.remoteSessionEventService.addEventListener(
            SessionMsg.SESSION_DISCONNECTED_MSG,
            (session: BlastWmks) => {
               this.onDisconnected(session.key);
            }
         );
      } else {
         RTAV.logService.debug("skip since remoteSessionEventService absent when creating RTAV service");
      }
   }

   public onConnecting(key, vvcSession, rtavInitArgs?) {
      RTAV.logService.debug("rtav service onConnecting");
      if (!!rtavInitArgs) {
         RTAV.logService.debug("when rtavService onConnecting rtavInitArgs.enableRTAVH264Codec value is " + rtavInitArgs.enableRTAVH264Codec);
         RTAV.logService.debug("when rtavService onConnecting rtavInitArgs.enableRTAVOpusCodec value is " + rtavInitArgs.enableRTAVOpusCodec);
         RTAV.logService.debug("when rtavService onConnecting rtavInitArgs.enableRTAVDTX value is " + rtavInitArgs.enableRTAVDTX);
         RTAV.logService.debug("when rtavService onConnecting rtavInitArgs.hardwareAccelerationOption value is " + rtavInitArgs.hardwareAccelerationOption);
         vvcSession.enableRTAVH264Codec = rtavInitArgs.enableRTAVH264Codec;
         vvcSession.enableRTAVOpusCodec = rtavInitArgs.enableRTAVOpusCodec;
         vvcSession.enableRTAVDTX = rtavInitArgs.enableRTAVDTX;
         vvcSession.hardwareAccelerationOption = rtavInitArgs.hardwareAccelerationOption;
      }
      return RTAV.wmksAdapter.addSession(key, vvcSession);
   }

   public onConnected(key: string) {
      return RTAV.wmksAdapter.activeSession(key);
   }

   public onDisconnected(key: string) {
      return RTAV.wmksAdapter.removeSession(key);
   }

   public onDeviceStatusChanged(callback) {
      return RTAV.resourceManager.onDeviceStatusChanged(callback);
   }

   public setDialogHandler(callback) {
      return RTAV.dialogService.setDialogFunction(callback);
   }

   public hasSession(key) {
      return RTAV.wmksAdapter.hasSession(key);
   }

   private _confirmRTAVSessionSwitch = (info, okCallback, cancelCallback) => {
      if (this.modalDialogService) {
         this.modalDialogService.showError({
            data: {
               titleKey: "RTAV_DEVICE_CONFLICT_T",
               contentKey: "RTAV_DEVICE_CONFLICT_M",
               buttonLabelConfirmKey: "OK"
            }
         });
         // call the cancelCallback here for we only need to show a error
         // dialog, and then we could send response to the agent.
         cancelCallback();
      }
   };
}
